/**
 * Wallets — live wallet balances, statements and adjustment proposals.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import {
  useAdminQuery,
  useDebouncedValue,
  downloadCsv,
  shortDateTime,
  tzs,
  errorMessage,
  type Paged,
} from "../lib/admin";
import { adminApi } from "../lib/api";
import type { AdminWallet } from "../lib/types";
import {
  Search,
  Download,
  Wallet,
  Lock,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";

interface StatementResponse {
  opening_balance_minor: number;
  closing_balance_minor: number;
  currency: string;
  lines: Array<{
    journal_ref: string;
    occurred_at: string;
    type: string;
    description: string;
    debit_minor: number;
    credit_minor: number;
    balance_minor: number;
    order_ref: string | null;
  }>;
  next_cursor: string | null;
}

export function Wallets() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminWallet | null>(null);
  const [direction, setDirection] = useState<"credit" | "debit">("credit");
  const [amount, setAmount] = useState("");
  const [reasonCode, setReasonCode] = useState("goodwill");
  const [note, setNote] = useState("");
  const [proposalRef, setProposalRef] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const pageSize = 25;

  const q = useDebouncedValue(search);
  const list = useAdminQuery<Paged<AdminWallet>>(
    ["wallets", q, statusFilter, page],
    "/admin/v1/wallets",
    { q: q || undefined, status: statusFilter === "all" ? undefined : statusFilter, page, page_size: pageSize },
    true,
    30_000,
  );

  const statement = useAdminQuery<StatementResponse>(
    ["wallet-statement", selected?.wallet_id],
    `/admin/v1/wallets/${selected?.wallet_id}/statement`,
    { limit: 50 },
    Boolean(selected),
    30_000,
  );

  const adjustment = useMutation({
    mutationFn: (input: { wallet_id: string; direction: "credit" | "debit"; amount_minor: number; reason_code: string; note: string }) =>
      adminApi.post<{ ref: string }>("/admin/v1/wallet-adjustments", undefined, {
        query: {
          wallet_id: input.wallet_id,
          direction: input.direction,
          amount_minor: input.amount_minor,
          reason_code: input.reason_code,
          note: input.note,
        },
      }),
    onSuccess: (proposal) => {
      setProposalRef(proposal.ref);
      setAmount("");
      setNote("");
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.meta?.total ?? list.data?.total ?? rows.length;
  const totalAvailable = rows.reduce((sum, w) => sum + w.available_minor, 0);
  const totalReserved = rows.reduce((sum, w) => sum + w.reserved_minor, 0);
  const totalCommission = rows.reduce((sum, w) => sum + w.commission_minor, 0);
  const blocked = rows.filter((w) => w.status !== "active").length;

  const ownerOf = (w: AdminWallet) => (w.owner_type === "superdealer" ? w.superdealer : w.agent);

  const exportCsv = () => {
    downloadCsv(
      "evocha-wallets.csv",
      ["Wallet", "Owner type", "Owner", "Phone", "Available", "Reserved", "Commission", "Status"],
      rows.map((w) => [
        w.wallet_id,
        w.owner_type,
        ownerOf(w)?.business_name ?? "",
        ownerOf(w)?.phone ?? "",
        w.available_minor / 100,
        w.reserved_minor / 100,
        w.commission_minor / 100,
        w.status,
      ]),
    );
  };

  const closeDrawer = () => {
    setSelected(null);
    setAmount("");
    setNote("");
    setProposalRef(null);
    setActionError(null);
  };

  return (
    <>
      <PageHead
        title="Wallets"
        description="Live agent and super-dealer wallet balances, statements, and adjustments."
        actions={
          <button className="ev-btn ev-btn-sm ev-btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
            <Download size={16} /> Export balances
          </button>
        }
      />

      <div className="admin-kpi-grid" style={{ marginBottom: 24 }}>
        <div className="admin-card">
          <span className="ev-body-muted">Available (this page)</span>
          <div className="ev-display-s" style={{ color: "var(--ev-mint-600)" }}>{tzs(totalAvailable)}</div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Reserved (this page)</span>
          <div className="ev-display-s" style={{ color: "var(--ev-amber-600)" }}>{tzs(totalReserved)}</div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Commission (this page)</span>
          <div className="ev-display-s" style={{ color: "var(--ev-blue-600)" }}>{tzs(totalCommission)}</div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Non-active wallets</span>
          <div className="ev-display-s" style={{ color: "var(--ev-red-600)" }}>{blocked}</div>
        </div>
      </div>

      <div className="admin-card-header admin-filter-bar" style={{ marginBottom: 16 }}>
        <div className="admin-filters" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", flex: 1 }}>
          <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ev-slate-400)", pointerEvents: "none" }} />
            <input
              className="ev-input"
              placeholder="Search agent or dealer, code, phone…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ paddingLeft: 36 }}
            />
          </div>
          <select className="ev-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
            <option value="frozen">Frozen</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {list.isLoading && <LoadingCard label="Loading wallets…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && rows.length === 0 && <EmptyState>No wallets found.</EmptyState>}

      {rows.length > 0 && (
        <>
          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-table-th">Wallet</th>
                    <th className="admin-table-th">Owner</th>
                    <th className="admin-table-th">Available</th>
                    <th className="admin-table-th">Reserved</th>
                    <th className="admin-table-th">Commission</th>
                    <th className="admin-table-th">Status</th>
                    <th className="admin-table-th" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w) => (
                    <tr key={w.wallet_id} className="admin-table-row-hover">
                      <td className="admin-table-td wallet-id-cell" data-label="Wallet"><span className="ev-body-mono ev-body-micro">{w.wallet_id}</span></td>
                      <td className="admin-table-td wallet-agent-cell" data-label="Owner">
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Wallet className="admin-cell-icon" size={16} style={{ color: "var(--ev-slate-400)" }} />
                          <div className="admin-cell-stack">
                            <div className="ev-body-semibold">
                              {ownerOf(w)?.business_name ?? w.wallet_id.slice(0, 8)}
                            </div>
                            <div className="ev-body-micro muted">
                              {ownerOf(w)?.phone ??
                                (w.owner_type === "superdealer" ? "Super dealer" : "Agent")}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-table-td" data-label="Available"><span className="ev-body-mono" style={{ color: "var(--ev-mint-600)" }}>{w.available_display}</span></td>
                      <td className="admin-table-td" data-label="Reserved"><span className="ev-body-mono" style={{ color: "var(--ev-amber-600)" }}>{w.reserved_display}</span></td>
                      <td className="admin-table-td" data-label="Commission"><span className="ev-body-mono" style={{ color: "var(--ev-blue-600)" }}>{w.commission_display}</span></td>
                      <td className="admin-table-td" data-label="Status">
                        <span className={`ev-badge ${w.status === "active" ? "ev-badge-mint" : "ev-badge-red"}`}>
                          {w.status === "active" ? <Wallet size={12} /> : <Lock size={12} />}
                          {w.status}
                        </span>
                      </td>
                      <td className="admin-table-td" data-label="">
                        <button className="ev-btn ev-btn-sm ev-btn-ghost" title="Open wallet" onClick={() => setSelected(w)}>
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {total > pageSize && (
            <div className="admin-pagination">
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft size={16} /> Previous
              </button>
              <span className="ev-body-micro">Page {page} · {total} wallets</span>
              <button className="ev-btn ev-btn-sm ev-btn-ghost" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {selected && (
        <div className="admin-drawer-overlay" onClick={closeDrawer}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()} style={{ width: 600, maxWidth: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ marginBottom: 4 }}>{ownerOf(selected)?.business_name ?? "Wallet"}</h3>
                <span className="ev-body-micro muted">{selected.wallet_id}</span>
              </div>
              <button className="ev-icon-btn" onClick={closeDrawer} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="admin-detail-grid" style={{ marginTop: 20 }}>
              <div>
                <div className="ev-body-micro muted">Available</div>
                <div className="ev-body-semibold" style={{ color: "var(--ev-mint-600)" }}>{selected.available_display}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Reserved</div>
                <div className="ev-body" style={{ color: "var(--ev-amber-600)" }}>{selected.reserved_display}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Commission</div>
                <div className="ev-body" style={{ color: "var(--ev-blue-600)" }}>{selected.commission_display}</div>
              </div>
            </div>

            <h4 style={{ marginTop: 24, marginBottom: 8 }}>Request adjustment</h4>
            {proposalRef ? (
              <div className="admin-note">
                <AlertTriangle size={16} />
                <span>Proposal {proposalRef} created. Approve it in the Approvals inbox.</span>
              </div>
            ) : (
              <>
                {actionError && (
                  <div className="admin-note" style={{ marginBottom: 8 }}>
                    <AlertTriangle size={16} />
                    <span>{actionError}</span>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <select
                    className="ev-select"
                    value={direction}
                    onChange={(e) => setDirection(e.target.value as "credit" | "debit")}
                  >
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                  </select>
                  <input
                    className="ev-input"
                    type="number"
                    min="1"
                    placeholder="Amount (TZS)"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    style={{ width: 150 }}
                  />
                  <input
                    className="ev-input"
                    placeholder="Reason code"
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}
                    style={{ width: 150 }}
                  />
                  <input
                    className="ev-input"
                    placeholder="Note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    style={{ flex: 1, minWidth: 160 }}
                  />
                  <button
                    className="ev-btn ev-btn-sm ev-btn-primary"
                    disabled={adjustment.isPending || Number(amount) <= 0}
                    onClick={() =>
                      adjustment.mutate({
                        wallet_id: selected.wallet_id,
                        direction,
                        amount_minor: Math.round(Number(amount) * 100),
                        reason_code: reasonCode,
                        note,
                      })
                    }
                  >
                    {direction === "credit" ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                    Propose
                  </button>
                </div>
              </>
            )}

            <h4 style={{ marginTop: 28, marginBottom: 8 }}>Statement</h4>
            {statement.isLoading && <LoadingCard label="Loading statement…" />}
            {statement.error && <ErrorCard error={statement.error} />}
            {statement.data && (
              <>
                <div style={{ display: "flex", gap: 24, marginBottom: 8 }}>
                  <span className="ev-body-micro muted">Opening {tzs(statement.data.opening_balance_minor)}</span>
                  <span className="ev-body-micro muted">Closing {tzs(statement.data.closing_balance_minor)}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
                  {statement.data.lines.length === 0 && <span className="ev-body-micro muted">No ledger lines yet.</span>}
                  {statement.data.lines.map((line) => (
                    <div key={line.journal_ref + line.occurred_at} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="ev-body-micro">{line.description || line.type}</div>
                        <div className="ev-body-micro muted">
                          {shortDateTime(line.occurred_at)} · {line.order_ref ?? line.journal_ref}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div className="ev-body-mono" style={{ color: line.credit_minor > 0 ? "var(--ev-mint-600)" : "var(--ev-red-600)" }}>
                          {line.credit_minor > 0 ? "+" : "−"}
                          {tzs(line.credit_minor > 0 ? line.credit_minor : line.debit_minor)}
                        </div>
                        <div className="ev-body-micro muted">{tzs(line.balance_minor)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
